import { useState, useEffect, useCallback } from "react";
import { products as mockProducts, wineCategories } from "@/mocks/products";
import type { Product, ProductFilter } from "@/types/product";
import ProductCard from "./components/ProductCard";
import ProductModal from "./components/ProductModal";
import { supabase } from "@/lib/supabase";
import { useUser } from "@/context/UserContext";

const stockFilterOptions = [
  { value: "all", label: "全部商品" },
  { value: "low", label: "即將售罄" },
  { value: "out", label: "已售罄" },
  { value: "inactive", label: "停用商品" },
];

// 以當天日期為種子的確定性庫存計算（0 ~ 50 瓶）
const getDailyStock = (id: string, max = 50): number => {
  const today = new Date().toISOString().split("T")[0];
  const seed = `${today}-${id}`;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % (max + 1);
};

// 將 DB 欄位 (snake_case) 轉為前端 Product 型別
function dbRowToProduct(row: Record<string, unknown>): Product {
  return {
    id: row.id as string,
    brand: (row.brand as string) || "",
    name: (row.name as string) || "",
    vintage: (row.vintage as string) || "",
    country: (row.country as string) || "",
    region: (row.region as string) || "",
    grape: (row.grape as string) || "",
    costPrice: (row.cost_price as number) || 0,
    sellingPrice: (row.selling_price as number) || 0,
    stock: row.created_by == null
      ? getDailyStock((row.id as string) || "unknown")
      : ((row.stock as number) ?? 0),
    safetyStock: (row.safety_stock as number) || 3,
    status: (row.status as "active" | "inactive") || "active",
    description: (row.description as string) || "",
    image: (row.image as string) || null,
    category: (row.category as string) || "紅酒",
    createdAt: (row.created_at as string) || "",
    createdByName: (row.created_by_name as string) || "",
    createdBy: (row.created_by as string) || null,
  };
}

export default function ProductsPage() {
  const user = useUser();
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [dbLoading, setDbLoading] = useState(true);
  const [filter, setFilter] = useState<ProductFilter>({
    search: "",
    category: "all",
    status: "all",
    stockAlert: "all",
    creatorSearch: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [authUserId, setAuthUserId] = useState<string | null>(null);

  // Get auth user id
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) setAuthUserId(data.user.id);
    });
  }, []);

  // Fetch products from DB based on role
  useEffect(() => {
    if (!authUserId) return;

    const fetchProducts = async () => {
      setDbLoading(true);
      try {
        let query = supabase.from("products").select("*");

        if (!user.isAdmin) {
          // 一般員工：看自己上架的 + 系統預設（created_by IS NULL）
          query = query.or(`created_by.eq.${authUserId},created_by.is.null`);
        }
        // Admin：看全部（不加 created_by 過濾）

        const { data, error } = await query.order("created_at", { ascending: false });

        if (error) throw error;

        if (data && data.length > 0) {
          const products = data.map(dbRowToProduct);
          setAllProducts(products);
        } else {
          // DB 無資料時 fallback mock
          const fallback = mockProducts.map((p) => ({
            ...p,
            stock: getDailyStock(p.id),
          }));
          setAllProducts(fallback);
        }
      } catch {
        // 查詢失敗 fallback mock
        const fallback = mockProducts.map((p) => ({
          ...p,
          stock: getDailyStock(p.id),
        }));
        setAllProducts(fallback);
      } finally {
        setDbLoading(false);
      }
    };

    fetchProducts();
  }, [authUserId, user.isAdmin]);

  // Apply filters
  const applyFilters = useCallback(() => {
    let result = [...allProducts];

    // Search (name/brand/country/region/grape)
    if (filter.search.trim()) {
      const term = filter.search.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.brand.toLowerCase().includes(term) ||
          p.country.toLowerCase().includes(term) ||
          p.region.toLowerCase().includes(term) ||
          p.grape.toLowerCase().includes(term)
      );
    }

    // Admin-only: creator search
    if (user.isAdmin && filter.creatorSearch?.trim()) {
      const creatorTerm = filter.creatorSearch.toLowerCase();
      result = result.filter((p) =>
        (p.createdByName || "").toLowerCase().includes(creatorTerm)
      );
    }

    // Category
    if (filter.category !== "all") {
      result = result.filter((p) => p.category === filter.category);
    }

    // Stock alert
    if (filter.stockAlert === "low") {
      result = result.filter((p) => p.stock <= p.safetyStock && p.stock > 0 && p.status === "active");
    } else if (filter.stockAlert === "out") {
      result = result.filter((p) => p.stock === 0 && p.status === "active");
    } else if (filter.stockAlert === "inactive") {
      result = result.filter((p) => p.status === "inactive");
    }

    setFilteredProducts(result);
  }, [allProducts, filter, user.isAdmin]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const showToast = (message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleAdd = () => {
    setEditingProduct(null);
    setIsModalOpen(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  const handleSave = async (product: Product) => {
    if (editingProduct) {
      const { error } = await supabase
        .from("products")
        .update({
          brand: product.brand,
          name: product.name,
          vintage: product.vintage,
          country: product.country,
          region: product.region,
          grape: product.grape,
          cost_price: product.costPrice,
          selling_price: product.sellingPrice,
          stock: product.stock,
          safety_stock: product.safetyStock,
          status: product.status,
          description: product.description,
          image: product.image,
          category: product.category,
        })
        .eq("id", product.id);

      if (error) {
        showToast("儲存失敗：" + error.message, "error");
        return;
      }

      setAllProducts((prev) => prev.map((p) => (p.id === product.id ? product : p)));
      showToast("商品已更新");
    } else {
      const { error } = await supabase.from("products").insert({
        brand: product.brand,
        name: product.name,
        vintage: product.vintage,
        country: product.country,
        region: product.region,
        grape: product.grape,
        cost_price: product.costPrice,
        selling_price: product.sellingPrice,
        stock: product.stock,
        safety_stock: product.safetyStock,
        status: product.status,
        description: product.description,
        image: product.image,
        category: product.category,
        created_by: authUserId,
        created_by_name: user.userName,
      });

      if (error) {
        showToast("新增失敗：" + error.message, "error");
        return;
      }

      setRequestSuccess(true);
      // 重新載入商品列表，讓新增的商品立即顯示
      if (authUserId) {
        let query = supabase.from("products").select("*");
        if (!user.isAdmin) {
          query = query.or(`created_by.eq.${authUserId},created_by.is.null`);
        }
        const { data } = await query.order("created_at", { ascending: false });
        if (data) {
          setAllProducts(data.map(dbRowToProduct));
        }
      }
    }
  };

  const handleDelete = (id: string) => {
    setDeleteConfirm(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    const { error } = await supabase.from("products").delete().eq("id", deleteConfirm);
    if (error) {
      showToast("刪除失敗：" + error.message, "error");
      return;
    }
    setAllProducts((prev) => prev.filter((p) => p.id !== deleteConfirm));
    setDeleteConfirm(null);
    showToast("商品已刪除");
  };

  // Stats
  const totalCount = allProducts.length;
  const activeCount = allProducts.filter((p) => p.status === "active").length;
  const lowStockCount = allProducts.filter(
    (p) => p.stock <= p.safetyStock && p.stock > 0 && p.status === "active"
  ).length;
  const outOfStockCount = allProducts.filter((p) => p.stock === 0 && p.status === "active").length;

  return (
    <div className="space-y-5 max-w-[1440px] mx-auto">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-top-2 ${
            toast.type === "error"
              ? "bg-accent-500 text-background-50"
              : "bg-foreground-800 text-background-50"
          }`}
        >
          <i
            className={`${toast.type === "error" ? "ri-error-warning-line" : "ri-check-line"} text-primary-400`}
          />
          {toast.message}
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground-900/40 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-background-50 rounded-xl border border-background-200 shadow-xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
                <i className="ri-alert-line text-accent-500" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground-900">確認刪除商品？</h3>
                <p className="text-xs text-foreground-500 mt-0.5">此操作無法復原</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 cursor-pointer transition-colors"
              >
                取消
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-accent-500 text-background-50 hover:bg-accent-600 cursor-pointer transition-colors"
              >
                確認刪除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Success Modal */}
      {requestSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground-900/40 backdrop-blur-sm" onClick={() => setRequestSuccess(false)} />
          <div className="relative bg-background-50 rounded-xl border border-background-200 shadow-xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                <i className="ri-check-line text-primary-500 text-lg" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground-900">商品上架成功</h3>
                <p className="text-xs text-foreground-500 mt-0.5">
                  商品已成功上架，您可以在列表中查看。
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end pt-2">
              <button
                onClick={() => setRequestSuccess(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer transition-colors"
              >
                確認
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1
            className="text-xl font-bold text-foreground-900"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            商品管理
          </h1>
          <p className="text-sm text-foreground-500 mt-1">
            {user.isAdmin
              ? "管理所有員工上架的商品，可依建立者搜尋檢視"
              : "管理您上架的紅酒商品，含產地、年份、價格及庫存狀態"}
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap shrink-0"
        >
          <i className="ri-add-line" />
          新增商品
        </button>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-background-50 border border-background-200 rounded-xl p-3">
          <p className="text-xs text-foreground-500">全部商品</p>
          <p className="text-lg font-bold text-foreground-900 mt-1">{totalCount}</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-3">
          <p className="text-xs text-foreground-500">上架中</p>
          <p className="text-lg font-bold text-primary-600 mt-1">{activeCount}</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-3">
          <p className="text-xs text-foreground-500">即將售罄</p>
          <p className="text-lg font-bold text-accent-600 mt-1">{lowStockCount}</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-3">
          <p className="text-xs text-foreground-500">已售罄</p>
          <p className="text-lg font-bold text-accent-500 mt-1">{outOfStockCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              type="text"
              value={filter.search}
              onChange={(e) => setFilter((prev) => ({ ...prev, search: e.target.value }))}
              placeholder="搜尋酒款名稱 / 酒莊 / 品種..."
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
            />
          </div>

          {/* Admin-only: Creator Search */}
          {user.isAdmin && (
            <div className="relative w-full sm:w-52 shrink-0">
              <i className="ri-user-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                type="text"
                value={filter.creatorSearch || ""}
                onChange={(e) => setFilter((prev) => ({ ...prev, creatorSearch: e.target.value }))}
                placeholder="依建立者名稱搜尋..."
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-secondary-400 focus:border-secondary-400"
              />
            </div>
          )}

          {/* Category Filter */}
          <div className="flex flex-wrap gap-1.5 shrink-0">
            <button
              onClick={() => setFilter((prev) => ({ ...prev, category: "all" }))}
              className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                filter.category === "all"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              全部
            </button>
            {wineCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter((prev) => ({ ...prev, category: cat }))}
                className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                  filter.category === cat
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
        {/* Stock Status Filter */}
        <div className="flex flex-wrap gap-1.5">
          {stockFilterOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setFilter((prev) => ({ ...prev, stockAlert: opt.value }))}
              className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                filter.stockAlert === opt.value
                  ? "bg-foreground-800 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Admin: Active creator filter badge */}
        {user.isAdmin && filter.creatorSearch?.trim() && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground-500">建立者篩選：</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium">
              <i className="ri-user-line text-secondary-600" />
              {filter.creatorSearch}
              <button
                onClick={() => setFilter((prev) => ({ ...prev, creatorSearch: "" }))}
                className="cursor-pointer hover:text-secondary-900"
              >
                <i className="ri-close-line" />
              </button>
            </span>
          </div>
        )}
      </div>

      {/* Result Count */}
      {filteredProducts.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-foreground-500">
            共 <span className="font-semibold text-foreground-900">{filteredProducts.length}</span> 筆結果
          </p>
          {(filter.search?.trim() || filter.category !== "all" || filter.stockAlert !== "all" || filter.creatorSearch?.trim()) && (
            <button
              onClick={() =>
                setFilter({
                  search: "",
                  category: "all",
                  status: "all",
                  stockAlert: "all",
                  creatorSearch: "",
                })
              }
              className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
            >
              清除所有篩選
            </button>
          )}
        </div>
      )}

      {/* Loading */}
      {dbLoading && (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200 rounded-xl">
          <i className="ri-loader-4-line animate-spin text-primary-500 text-3xl" />
          <p className="text-sm text-foreground-500 mt-3">載入商品中...</p>
        </div>
      )}

      {/* Product Grid */}
      {!dbLoading && filteredProducts.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isAdmin={user.isAdmin}
              currentUserId={authUserId}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!dbLoading && filteredProducts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200 rounded-xl">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-search-line text-foreground-300 text-2xl" />
          </div>
          <p className="text-sm font-medium text-foreground-500">
            {user.isAdmin
              ? "找不到符合條件的商品"
              : "您尚未上架任何商品"}
          </p>
          <p className="text-xs text-foreground-400 mt-1">
            {user.isAdmin
              ? "試試調整搜尋關鍵字、建立者名稱或篩選條件"
              : "點擊「新增商品」開始上架您的第一支酒款"}
          </p>
          {!user.isAdmin && (
            <button
              onClick={handleAdd}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line" />
              新增第一支商品
            </button>
          )}
        </div>
      )}

      {/* Product Modal */}
      <ProductModal
        isOpen={isModalOpen}
        product={editingProduct}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
      />
    </div>
  );
}